/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Text, UnstyledButton } from '@/shared/ui';
import type { OverviewMode } from '@/services/housien/dashboard';

type TFn = (key: string, options?: Record<string, unknown>) => string;

const OPTIONS: OverviewMode[] = ['basic', 'advanced'];

/* `flex-1` only bites on the phone, where the group is told to stretch;
   beside a title on a wide screen the group is `flex-none` and each half
   stays as wide as its word. */
const BASE = 'flex-1 rounded-(--radius-md) px-(--space-3) py-[0.3rem] text-(length:--text-body-sm) transition-colors duration-150';

/**
 * Two words and a moving background, not a switch.
 *
 * A switch asks "advanced: on or off", which forces the reader to work out
 * what the off state is called before they can answer. Both names on
 * screen at once means the choice is read rather than deduced, and the
 * width never changes as it flips.
 *
 * @param props.t - Translation function
 * @param props.mode - The mode currently on screen
 * @param props.onChange - Called with the mode the reader picked
 */
export function OverviewModeToggle({
  t, mode, onChange,
}: {
  t: TFn;
  mode: OverviewMode;
  onChange: (mode: OverviewMode) => void;
}) {
  return (
    <div
      role="group"
      aria-label={t('dashboard.mode.label')}
      data-testid="overview-mode-toggle"
      className="flex flex-none items-center gap-[2px] rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-secondary) p-[2px] max-md:min-w-0 max-md:flex-1"
    >
      {OPTIONS.map((option) => {
        const active = option === mode;
        return (
          <UnstyledButton
            key={option}
            type="button"
            // `aria-pressed` rather than a radio group: these are two
            // states of one control, and a screen reader announcing
            // "básico, selected" beside "avançado, not selected" says the
            // same thing the highlight does.
            aria-pressed={active}
            data-testid={`overview-mode-${option}`}
            onClick={() => !active && onChange(option)}
            className={`${BASE} ${active
              ? 'bg-[var(--surface-raised)] [font-weight:var(--font-semibold)] text-[var(--text-primary)] shadow-[var(--shadow-sm)]'
              : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}
          >
            <Text as="span">{t(`dashboard.mode.${option}`)}</Text>
          </UnstyledButton>
        );
      })}
    </div>
  );
}
